import React, { useContext, useEffect } from 'react'
import { ModalActions } from '../../common/modal/Modal'
import { ControlledTextInput } from '../../common/inputs/TextInput'
import { Button, makeStyles, shorthands, tokens } from '@fluentui/react-components'
import { useForm } from 'react-hook-form'
import { RiskAnalysis, RiskAnalysisType, RiskManagementModules } from '../../../api/schemas/schema'
import { DataContext } from '../../../context/DataContext'
import { t } from '../../../i18n/i18n'
import {
   addConsequenceAndProbabilityRisk,
   addSimplifiedRisk,
   deleteConsequenceAndProbabilityRisk,
   deleteSimplifiedRisk,
   updateConsequenceAndProbabilityRisk,
   updateSimplifiedRisk,
} from '../../../helpers/riskAnalysisHelper'
import { IItemState } from '../../../interfaces/IItemState'
import { areModulesActiveInOrganisation } from '../../../helpers/organisationHelper'
import { DraggableModal } from '../../common/modal/DraggableModal'
import { isSystemAdmin } from '../../../helpers/permissionHelper'
import { MoreOptionsMenuButton } from '../../common/other/MoreOptionsMenuButton'
import { Form } from '../../common/form/Form'
import { ControlledDropDownClearable } from '../../common/inputs/ControlledDropdown'
import { ControlledProtectedAssetGoalDropdown } from '../../common/inputs/ControlledProtectedAssetGoalDropdown'

const useStyles = makeStyles({
   form: {
      display: 'flex',
      flexDirection: 'column',
      ...shorthands.gap(tokens.spacingHorizontalXL),
   },
   dismissIcon: {
      padding: `9px 0`,
      marginRight: '8px',
   },
})

type ModalRiskProps = {
   setRiskAnalysisState: React.Dispatch<React.SetStateAction<IItemState<RiskAnalysis>>>
   setOpen: React.Dispatch<React.SetStateAction<boolean>>
   open: boolean
   analysis: RiskAnalysis
   selectedItem: any
   lastUsedAssetGoalId: string | null
   setLastUsedAssetGoalId: React.Dispatch<React.SetStateAction<string | null>>
   lastUsedThreatId: string | null
   setLastUsedThreatId: React.Dispatch<React.SetStateAction<string | null>>
   disabled?: boolean
}
export const ModalRisk = ({
   setRiskAnalysisState,
   setOpen,
   open,
   analysis,
   selectedItem,
   lastUsedAssetGoalId,
   setLastUsedAssetGoalId,
   lastUsedThreatId,
   setLastUsedThreatId,
   disabled,
}: ModalRiskProps) => {
   const classes = useStyles()
   const dataContext = useContext(DataContext)
   const { userAccount, protectedAssetGoals, organisation } = dataContext.state
   const {
      control,
      setValue,
      handleSubmit,
      reset,
      formState: { isDirty, isSubmitting },
   } = useForm<any>({ disabled: disabled })

   const areModulesActive = areModulesActiveInOrganisation(organisation, [
      RiskManagementModules.SIMPLIFIED_RISK_ANALYSIS,
      RiskManagementModules.CONSEQUENCE_AND_PROBABILITY_ANALYSIS,
   ])

   const onSubmit = async (data: any) => {
      setLastUsedThreatId(data.threatId)
      setLastUsedAssetGoalId(data.protectedAssetGoalId)

      if (analysis.type === RiskAnalysisType.ConsequenceAndProbabilityAnalysis) {
         if (data.id) {
            await updateConsequenceAndProbabilityRisk(setRiskAnalysisState, close, dataContext, data)
            reset(data)
         } else {
            await addConsequenceAndProbabilityRisk(setRiskAnalysisState, close, dataContext, data, analysis)
         }
      } else if (analysis.type === RiskAnalysisType.SimplifiedRiskAnalysis) {
         if (data.id) {
            await updateSimplifiedRisk(setRiskAnalysisState, close, dataContext, data)
            reset(data)
         } else {
            await addSimplifiedRisk(setRiskAnalysisState, close, dataContext, data, analysis)
         }
      }
   }

   const close = () => {
      reset()
      setOpen(false)
   }

   useEffect(() => {
      if (open) {
         if (selectedItem) {
            reset({
               ...selectedItem,
               protectedAssetGoalId: selectedItem.protectedAssetGoal?.id ?? null,
               threatId: selectedItem?.threat?.id ?? null,
            })
         } else {
            const defaultAssetGoal = protectedAssetGoals.find((p) => p.id === lastUsedAssetGoalId) ?? null
            const defaultThreat = analysis.threats?.find((t) => t.id === lastUsedThreatId) ?? null

            reset({
               scenario: null,
               protectedAssetGoal: defaultAssetGoal,
               protectedAssetGoalId: lastUsedAssetGoalId ?? null,
               threat: defaultThreat,
               threatId: lastUsedThreatId ?? null,
               source: null,
               currentState: null,
               consequence: null,
               underlyingMotivation: null,
            })
         }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [selectedItem, open, reset, protectedAssetGoals, analysis.threats])

   const deleteOptions = () => {
      const isConsequenceRisk = analysis.type === RiskAnalysisType.ConsequenceAndProbabilityAnalysis

      const riskInUse = analysis.actionPlans?.some((ap) => ap.riskId === selectedItem.id)
      const userHasPermission = isSystemAdmin(userAccount) || analysis.analysisOwner?.id === userAccount?.id
      const deleteDisabled = !userHasPermission || riskInUse
      const deleteTooltipMessage = deleteDisabled
         ? !userHasPermission
            ? t('OnlySystemAdministratorOrRiskOwnerCanDeleteItem', { Item: t('Risk').toLowerCase() })
            : t('ItemIsConnectedToItemAndCantBeDeleted', { ItemA: t('TheRisk'), Article: t('A').toLowerCase(), ItemB: t('ActionPlan').toLowerCase() })
         : ''

      return (
         <MoreOptionsMenuButton
            item={selectedItem}
            deleteFunction={isConsequenceRisk ? deleteConsequenceAndProbabilityRisk : deleteSimplifiedRisk}
            setState={setRiskAnalysisState}
            closeOnDelete={close}
            disabled={deleteDisabled}
            disabledToolTipMessage={deleteTooltipMessage}
            deleteMessage={t('DeleteItem', { Item: t('Risk').toLowerCase() })}
            deleteConfirmationMessage={t('DeleteItemConfirmationMessage', { Item: t('TheRisk').toLocaleLowerCase() })}
         />
      )
   }

   return (
      <DraggableModal
         title={selectedItem ? t('EditRisk') : t('IdentifyRisk')}
         open={open}
         onClose={close}
         secondaryButton={selectedItem ? deleteOptions : null}
      >
         <Form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
            {analysis.type === RiskAnalysisType.ConsequenceAndProbabilityAnalysis && (
               <ControlledDropDownClearable
                  name={'threat'}
                  control={control}
                  label={t('CurrentThreat')}
                  items={analysis.threats}
                  setValue={setValue}
                  setValueString={'threatId'}
                  disabled={disabled}
               />
            )}
            <ControlledProtectedAssetGoalDropdown
               name={'protectedAssetGoal'}
               control={control}
               label={t('ProtectedAsset/OrganisationalGoal')}
               items={protectedAssetGoals}
               setValue={setValue}
               setValueString={'protectedAssetGoalId'}
               disabled={disabled || !areModulesActive}
               showTooltip={!areModulesActive}
               tooltipMsg={t('FeatureRequiresConsequenceRiskOrSimplifiedRisk')}
            />
            <ControlledTextInput control={control} name={'scenario'} label={t('RiskScenario')} multiline disabled={disabled} required />
            <ControlledTextInput control={control} name={'consequence'} label={t('ConsequenceDescription')} multiline />
            <ControlledTextInput control={control} name={'source'} label={t('RiskSource')} multiline />
            <ControlledTextInput control={control} name={'currentState'} label={t('WeaknessesOrCurrentProtection')} multiline />
            <ControlledTextInput control={control} name={'underlyingMotivation'} label={t('UnderlyingMotivationAndStand')} multiline />

            <ModalActions>
               <Button onClick={close} disabled={isSubmitting}>
                  {disabled ? t('Close') : t('Cancel')}
               </Button>
               {!disabled && (
                  <Button appearance="primary" type="submit" disabled={!isDirty || isSubmitting}>
                     {t(selectedItem ? 'Update' : 'Save')}
                  </Button>
               )}
            </ModalActions>
         </Form>
      </DraggableModal>
   )
}
